import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { MUser } from '@/src/modules/user/models/user';
import { TBiodataInput, TBpjsInput } from '../../user/data/schemas/input';
import { MIndividual } from '../../user/models/individual';

type TState = {
  user: MUser | null;
  individual: MIndividual | null;
  status: 'idle' | 'loading' | 'success' | 'error';
};

type TAction = {
  setUser: (user: MUser, individual: MIndividual) => void;
  setBiodata: (biodata: TBiodataInput) => void;
  setBpjs: (bpjs: TBpjsInput) => void;
  setStatus: (status: TState['status']) => void;
  setProfilePicture: (profilePicture: string) => void;
  resetState: () => void;
};

export const useAuthStore = create<TState & TAction>()(
  immer((set) => ({
    user: null,
    individual: null,
    status: 'idle',
    setUser: (user: MUser, individual: MIndividual) =>
      set(() => ({
        user,
        individual,
        status: user ? 'success' : 'error',
      })),
    setBiodata: (biodata: TBiodataInput) =>
      set((state) => {
        if (!state.user || !state.individual) {
          return state;
        }

        const [firstName, ...rest] = biodata.full_name.trim().split(' ');
        const lastName = rest.join(' ');
        const updatedUser = new MUser({
          ...state.user.props,
          firstName,
          lastName,
          phoneNumber: biodata.whatsapp,
          email: biodata.email,
        });

        const updatedIndividual = {
          ...state.individual,
          props: {
            ...state.individual.props,
            nickname: biodata.nickname,
            birthDate: biodata.birthdate,
            gender: biodata.gender,
          },
        };

        return {
          user: updatedUser,
          individual: updatedIndividual,
        };
      }),
    setBpjs: (bpjs: TBpjsInput) => {
      set((state) => ({
        individual: {
          ...state.individual,
          props: {
            ...state.individual?.props,
            bpjsHealth: bpjs.bpjs_number ?? null,
            bpjsEmployment: bpjs.bpjs_ketenagakerjaan_number ?? null,
          },
        },
      }));
    },
    setProfilePicture: (profilePicture: string) => {
      set((state) => ({
        individual: {
          ...state.individual,
          props: {
            ...state.individual?.props,
            profileImage: profilePicture,
          },
        },
      }));
    },
    setStatus: (status: TState['status']) =>
      set((state) => {
        state.status = status;
      }),
    resetState: () => {
      set((state) => {
        state.user = null;
        state.individual = null;
        state.status = 'idle';
      });
    },
  }))
);
